/**
 * OpenAICredentialModal — paste-token form for the user's OpenAI API
 * key, stored in workspace-credentials and read at deploy time by the
 * Codex provider gate (resolveAgentCredential with provider='codex' in
 * backend/src/handlers/apps.js).
 *
 * Mirror of ClaudeCredentialModal, slimmed because Codex v1 is API-key
 * only — no OAuth flow (Codex CLI's ~/.codex/auth.json is out of scope).
 * One input field; type is hardcoded 'api' and provider='openai' on the
 * POST. Same reset-on-open behavior + stopPropagation guards as the
 * Claude modal so the two feel like siblings.
 *
 * onConnected({ suffix }) — caller uses this to refresh "now using
 * …abc123" affordances inline (Deploy modal Codex State A line,
 * Settings card status, etc).
 */

import React, { useEffect, useState } from 'react';
import { IonModal, IonButton, IonSpinner } from '@ionic/react';
import WorkspaceCredentials from '../../services/workspaceCredentials';
import '../../pages/IntegrationsPage/IntegrationsPage.css';

export default function OpenAICredentialModal({ isOpen, onClose, onConnected }) {
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setToken('');
      setError('');
      setSubmitting(false);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    const trimmed = token.trim();
    if (!trimmed) {
      setError('API key is required.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await WorkspaceCredentials.add({
        type: 'api',
        provider: 'openai',
        token: trimmed,
      });
      const suffix = res?.credential?.masked || '';
      onConnected?.({ suffix });
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Failed to save OpenAI API key. Please check the key and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={() => onClose?.()}
      className="lark-connect-modal"
      aria-label="Connect OpenAI"
    >
      <div
        className="lark-modal-content"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2>Connect OpenAI</h2>
        <p className="lark-modal-description">
          Zibby needs an OpenAI API key to deploy apps with the Codex
          agent. Keys are KMS-encrypted at rest and only decrypted
          inside your per-instance Fargate task. Mint one at{' '}
          <a
            href="https://platform.openai.com/api-keys"
            target="_blank"
            rel="noreferrer noopener"
            style={{ color: 'var(--ion-color-primary)' }}
          >
            platform.openai.com/api-keys
          </a>.
        </p>

        <div className="lark-form-group">
          <label className="lark-form-label" htmlFor="openai-cred-token">
            API key
          </label>
          <input
            id="openai-cred-token"
            type="password"
            value={token}
            placeholder="sk-…"
            onChange={(e) => { setToken(e.target.value || ''); setError(''); }}
            className="lark-form-input"
            disabled={submitting}
            autoComplete="off"
            spellCheck={false}
            aria-invalid={error ? 'true' : 'false'}
          />
        </div>

        {error && <p className="lark-modal-error" role="alert">{error}</p>}

        <div className="lark-modal-buttons">
          <IonButton
            expand="block"
            onClick={handleSubmit}
            disabled={submitting || !token.trim()}
            aria-label="Save OpenAI API key"
          >
            {submitting ? <IonSpinner name="crescent" /> : 'Save'}
          </IonButton>
          <IonButton
            expand="block"
            fill="clear"
            onClick={() => onClose?.()}
            disabled={submitting}
            aria-label="Cancel OpenAI credential entry"
          >
            Cancel
          </IonButton>
        </div>
      </div>
    </IonModal>
  );
}
